import {
  AnimaticEvaporatorConfig,
  LabelConfig,
  LabelConfigVariant,
  LabelSumRef,
} from '@repo/sdk/models/components';

import { parseLabelRef } from '@/features/equipment-browser/animatics/animatics.helpers';
import {
  AnimaticLabel,
  AnimaticLabelMap,
  LabelConfigMap,
} from '@/features/equipment-browser/animatics/animatics.types';
import { getRefValue } from '@/features/equipment-browser/utils';
import { computeDefrostGauge } from '@/helpers/defrost-gauge/compute-defrost-gauge';
import { secondsToDuration } from '@/helpers/unit-formatter/shared/utils';

// Computes the duration from the summable references in seconds
const computeDuration = (sumRefs: LabelSumRef[]): number => {
  let sum = 0;
  for (const ref of sumRefs) {
    const value = getRefValue(ref.ref);
    if (value && typeof value === 'number') {
      if (ref.negative) {
        sum -= value;
      } else {
        sum += value;
      }
    }
  }
  return sum;
};

// Formats the duration label. Stage/state text prefix comes from BFF via labelConfig.value;
// this function only appends the computed duration.
const formatDurationLabel = (labelConfig: LabelConfig): string => {
  let duration = 0;
  if (labelConfig.refSum) {
    duration = computeDuration(labelConfig.refSum);
  }
  const durationLabel = secondsToDuration(duration, true);
  if (labelConfig.value) {
    return `${labelConfig.value} · ${durationLabel}`;
  }
  return durationLabel;
};

// This a variant of the getAnimaticLabels function for the evaporator animatic which have complex defrost labels
export const getEvaporatorAnimaticLabels = (labelConfigs: LabelConfigMap): AnimaticLabelMap => {
  const animaticLabels: { [key: string]: AnimaticLabel } = {};
  Object.entries(labelConfigs).forEach(([key, labelConfig]) => {
    if (labelConfig.isBanner) {
      return;
    }
    const animaticLabel: AnimaticLabel = {
      text: labelConfig.value ?? '',
      variant: labelConfig.variant ?? LabelConfigVariant.Primary,
    };
    animaticLabel.text = labelConfig.value ?? '';
    if (labelConfig.ref) {
      animaticLabel.text = parseLabelRef(labelConfig.ref, labelConfig.displayUnits);
    }
    if (labelConfig.refSum) {
      animaticLabel.text = formatDurationLabel(labelConfig);
    }
    animaticLabels[key] = animaticLabel;
  });

  return animaticLabels;
};

type DefrostConfig = {
  defrostPercentage: number;
  defrostTickPercentages: number[];
};

export const getDefrostConfig = (config: AnimaticEvaporatorConfig): DefrostConfig => {
  const totalDurationSeconds = config.totalDefrostDurationRef
    ? getRefValue(config.totalDefrostDurationRef)
    : 0;

  const stageDurationsSeconds = config.defrostTickRefs?.map((ref) => getRefValue(ref)) ?? [];

  const elapsedDurationSeconds =
    config.currentDurationRefs?.reduce((acc, curr) => acc + (getRefValue(curr) as number), 0) ?? 0;

  return computeDefrostGauge({
    totalDurationSeconds,
    stageDurationsSeconds,
    elapsedDurationSeconds,
  });
};
